<template>
  <div class="dropzone" ref="dropzone" :class="{ 'notAllowed' : !allowedToDrop, 'loading': isLoading }">
    <input type="file" ref="buttonUpload" v-show="false" @change="uploadFiles(false)" multiple>
      <!-- Searchfield -->
      <div class="q-mb-md" style="flex-grow: 1">
        <q-input
          color="grey-5"
          v-model="filters.filename"
          @input="applyFilters"
          :placeholder="getString('module.library.list.filter.label')"
        />
      </div>
    <div class="q-table" id="libraryTable">
      <vuetable
        ref="vuetable"
        class="table-block q-mb-md"
        :class="{ 'is-loading': isLoading }"
        :table-height="tableHeight"
        :api-url="apiUrl"
        pagination-path
        :api-mode="true"
        :css="css.table"
        :noDataTemplate="getString('module.library.table.nothingFound')"
        :http-fetch="fetchData"
        :fields="columns"
        :row-class="applyRowClass"
        @vuetable:loading="onLoading"
        @vuetable:load-success="onSuccess"
        @vuetable:load-error="onError"
        @vuetable:row-clicked="onRowClicked">
        <!-- Custom fields -->
        <!-- File slot -->
        <div
          slot="file"
          class="file-slot"
          slot-scope="props"
            @mouseleave="displayFileInfo(...arguments, null)"
            @mouseover="displayFileInfo(...arguments, props.rowData)"
            >
          <div class="text-ellipsis text-weight-bold" :title="props.rowData.name">
              <span v-if="!props.rowData.edit"><q-icon size="large" :name="mapIcons(props.rowData.ext)" /> <span v-html="highlight(props.rowData.name, filters.filename)"></span></span>
              <span v-else><q-icon size="large" :name="mapIcons(props.rowData.ext)" /> <span>{{propertyName}}</span></span>
          </div>
        </div>
        <!-- Size slot -->
        <div slot="size" slot-scope="props"
          @mouseleave="displayFileInfo(...arguments, null)"
          @mouseover="displayFileInfo(...arguments, props.rowData)"
          >
          <div v-if="props.rowData.size || props.rowData.size === 0" v-text="readableBytes(props.rowData.size)"></div>
        </div>
        <!-- lastUpdate slot -->
        <div slot="lastUpdate" slot-scope="props"
          @mouseleave="displayFileInfo(...arguments, null)"
          @mouseover="displayFileInfo(...arguments, props.rowData)"
          >
          <div v-if="props.rowData.update" class="text-ellipsis" v-html="tableFormatTime(props.rowData.update)"></div>
        </div>
        <!-- Datetime online -->
        <div slot="datetime-online" slot-scope="props"
          @mouseleave="displayFileInfo(...arguments, null)"
          @mouseover="displayFileInfo(...arguments, props.rowData)"
          >
          <div class="text-ellipsis" v-html="(props.rowData.datetime && props.rowData.status !== 'draft') ?
            tableFormatTime(props.rowData.datetime) :
            'Concept'
            ">
          </div>
        </div>
      </vuetable>
    </div>
    <div @mouseover="displayFileInfo(null, hoverRow)" @mouseleave="displayFileInfo(null, null)" class="library-table__file-info" v-if="hoverRow" :style="`top: ${hoverTop}px; left: ${hoverLeft}px`">
      <div class="library-table__file-info--row">
        <span class="library-table__file-info--bold">{{ getString('module.library.details.file.title') }}:</span> {{ hoverRow.title || '-' }}
      </div>
      <div class="library-table__file-info--row">
        <span class="library-table__file-info--bold">{{ getString('module.library.details.file.altText') }}:</span> {{ hoverRow.alt || '-' }}
      </div>
      <div class="library-table__file-info--row">
        <span class="library-table__file-info--bold">{{ getString('module.library.details.file.description') }}:</span> {{ hoverRow.description || '-' }}
      </div>
      <div class="library-table__file-info--row" v-if="hoverRow.type === 'image'">
        <span class="library-table__file-info--bold">{{ getString('module.library.details.file.caption') }}:</span> {{ hoverRow.caption || '-' }}
      </div>
      <div class="library-table__file-info--row" v-if="hoverRow.type === 'image'">
        <span class="library-table__file-info--bold">Focuspoint:</span> x: {{ hoverRow.focuspoint.x }}, y: {{ hoverRow.focuspoint.y }}
      </div>
    </div>
  </div>
</template>

<script>
import Vuetable from 'vuetable-2';
import EventBus from '@/event-bus';
import qs from 'qs';
import _debounce from 'lodash.debounce';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import fetchData from '@/mixins/tables/fetchData';
import selectRows from '@/mixins/tables/selectRows';
import searchHighlight from '@/mixins/tables/searchHighlight';
import upload from '@/mixins/library/upload';
import dragAndDrop from '@/mixins/library/dragAndDrop';
import table from '@/mixins/library/table';
import common from '@/mixins/library/common';
import filtersMixin from '@/mixins/tables/filtersMixin';

// Helpers
import getString from '@/helpers/GetStrings';

export default {
  mixins: [
    tableMixin,
    pagination,
    fetchData,
    selectRows,
    upload,
    dragAndDrop,
    table,
    common,
    searchHighlight,
    filtersMixin,
  ],

  components: {
    Vuetable,
  },

  props: {
    sharedState: {
      type: Boolean,
      default: false,
    },
    // Used to calculated table height.
    spacing: {
      type: String,
      default: '178px',
    },
    useStoreFilters: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    // Restrict of a filetype eg. (file, image). When not set, all files are selectable. (eventually useful for filtering)
    fileType: {
      type: String,
      default: null,
    },
    // Further limit on file extension. eg (pdf, jpg)
    extension: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      prompt: false,
      filters: {
        filename: '',
      },
      // Drag and drop
      dragFiles: [],
      dragging: false,
      folders: [],
      files: [],
      activeDir: {},
      tableContext: 'folder',
      tableHeight: `calc(100vh - ${this.spacing})`,
      path: '/library',
      icons: {
        pdf: 'picture_as_pdf',
        jpg: 'image',
        jpeg: 'image',
        png: 'image',
        svg: 'image',
        tiff: 'image',
        txt: 'mdi-file-document-box-outline',
        doc: 'mdi-file-word',
        docx: 'mdi-file-word',
        gif: 'gif',
        xls: 'mdi-file-excel',
        zip: 'mdi-zip-box',
        parent: 'subdirectory_arrow_right',
      },
      rowSelectionDetail: [],
      currentFolderSelected: null,
      parentId: null,
      hoverRowId: null,
      hoverRow: null,
      hoverTop: 0,
      hoverLeft: 0,
      // Vuetable
      columns: [
        {
          name: '__slot:file',
          title: getString('file'),
          titleClass: 'text-left sortable vcol-l',
          dataClass: 'text-left vcol-l file text-ellipsis',
        },
        {
          name: '__slot:size',
          title: getString('size'),
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s size text-ellipsis',
        },
        {
          name: '__slot:lastUpdate',
          title: getString('lastUpdate'),
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s lastUpdate text-ellipsis',
          callback: 'tableFormatTime',
        },
      ],
    };
  },

  computed: {
    allowedToDrop() {
      return this.apiUrl !== '/library';
    },
    apiUrl: {
      get() {
        if (this.sharedState) {
          return this.$store.state.libraryStore.libraryPath;
        }
        return this.path;
      },
      set(value) {
        this.$store.dispatch('setLibraryPath', value);
        this.path = value;
      },
    },

  },

  methods: {
    getString,
    displayFileInfo: _debounce(function (event, row) {
      if (!row || row.type === 'regular') {
        this.hoverRow = null;
        this.hoverRowId = null;
        return;
      }
      if (this.hoverRowId !== row.id) {
        const y = event && event.clientY ? event.clientY : null;
        const x = event && event.clientX ? event.clientX : null;
        this.hoverTop = y;
        this.hoverLeft = x > window.innerWidth - 200 ? x - 200 : x;
        const location = row.location.replace('api/', '');
        this.axios.get(location).then((res) => {
          this.hoverRow = res.data.file;
        }).catch((err) => {
          console.warn(err);
        });
      }
      this.hoverRowId = row.id;
    }, 200),
    // Give back right icons in filebrowser
    mapIcons(type) {
      let icon = '';
      if (!type) {
        icon = 'folder';
      } else {
        icon = (this.icons[type]) ? this.icons[type] : 'mdi-file';
      }
      return icon;
    },

    onRowClicked(row, event) {
      if (row.ext && this.extension && row.ext !== this.extension) {
        if (row.ext !== 'parent') {
          return;
        }
      }
      if (row.type && this.fileType && row.type !== this.fileType) {
        if (row.type !== 'regular') {
          return;
        }
      }
      this.setDraggableRows();
      if (this.dragging) {
        this.targetIsFolder = row.type === 'regular' || row.ext === 'parent';
        if (this.targetIsFolder && this.dropTarget !== row.id) {
          this.dropTarget = row.id;
        } else if (row.ext && row.ext !== 'parent') {
          this.dropTarget = null;
        }

        return;
      }

      if (row.ext === 'parent') {
        const parentTargetArray = row.location.split('/').filter(x => x !== '').slice(0);
        parentTargetArray.length > 0 && this.fetchContent(row);
        EventBus.$emit('library-table:tree-push', 'parent');
      }

      if (this.rowSelection.length === 1 && this.rowSelection[0] === row.id && row.type === 'regular' && !event.metaKey) {
        this.fetchContent(row);
        EventBus.$emit('library-table:tree-push', row.name);
        this.$nextTick(() => {
          this.rowSelection = [];
        });
      }
      if (this.rowSelection.length === 1 && this.rowSelection[0] === row.id && row.ext) {
        if (!this.readonly) {
          this.goToDetail(row);
        }
      }

      if (this.selectable) {
        if (this.readonly && row.ext) {
          // Prevent going to detail view from the content editor. (build a preview instead)
          EventBus.$emit('library-table:preview-image', row);
        }
        this.selectRows(row, event);
      }

      if (this.rowSelection.length === 1) {
        this.rowSelection.forEach((index) => {
          this.currentFolderSelected = this.locations[index];
          EventBus.$emit('library-table:rowDetail', this.currentFolderSelected);
        });
      } else {
        this.currentFolderSelected = null;
        EventBus.$emit('library-table:rowDetail', null);
      }
    },

    goToDetail(rowData) {
      console.log(rowData);
      const filename = encodeURIComponent(rowData.location.replace('api/library/', ''));
      this.$router.push(`/library/${filename}`);
    },

    // Replace tableMixin onSuccess method
    onSuccess(response) {
      if (response.data && response.data.folder && response.data.folder.meta && response.data.folder.meta.parent_id) {
        this.parentId = response.data.folder.meta.parent_id;
      } else {
        this.parentId = null;
      }

      // Empty locations array
      this.locations = [];

      // Set loadingstate to false
      this.isLoading = false;
      /**
       * Modify the response object to fit vuetable.
       *
       * If response.data.folder exists we are dealing with the library
       * If response.data.folder is an array we can just pass it as it is
       * If response.data.folder is an object we have to check on two properties(folders-array and files-array)
       * and concat them
       */
      if (Array.isArray(response.data.folder)) {
        response.data.data = response.data.folder;
      } else {
        const folders = response.data.folder && response.data.folder.folders ? response.data.folder.folders : [];
        const files = response.data.folder && response.data.folder.files ? response.data.folder.files : [];

        this.folders = folders;
        this.files = files;

        response.data.data = folders.concat(files);
      }

      const parentTargetArray = this.apiUrl.split('/').filter(x => x !== '').slice(0);
      parentTargetArray.pop();

      const parentTarget = parentTargetArray.join('/');

      // Add id to items
      response.data.data.forEach((item, index) => {
        // Each row gets index + 1 since index 0 is reserved for parent directory.

        item.folder_id = item.type === 'regular' ? item.id : null;
        item.id = parentTargetArray.length > 0 ? index + 1 : index;

        this.locations.push(item);
      });

      if (parentTargetArray.length > 0 && this.filters.filename.length < 2) {
        this.locations.unshift({ ext: 'parent', name: '...', location: parentTarget });
        // Add a row which represents the parent directory for the current folder
        response.data.data.unshift({ id: 0, ext: 'parent', name: '...', location: parentTarget, parent_id: this.parentId });
      }

      // Return all rows to opacity 1 (undo reduced opacity for moved files)
      const rows = document.getElementsByTagName('tr');
      [].forEach.call(rows, (elem) => {
        elem.style.opacity = '1';
      });

      // For selectable tables, map row ids to an array.
      if (this.selectable) {
        this.rowData = response.data.data.map(row => row.id);
      }
    },

    // Overwrites select rows mixin.
    selectRows(row, event) {
      if (row.ext === 'parent') {
        return;
      }

      // If metaKey is pressed & the row is not in the selection, add to selection
      if (!this.rowSelection.includes(row.id) && event.metaKey) {
        this.rowSelection.push(row.id);

      // If metaKey is pressed & the row is already in the selection, remove from selection.
      } else if (this.rowSelection.includes(row.id) && event.metaKey) {
        this.rowSelection.splice(this.rowSelection.indexOf(row.id), 1);

      // When using shift select, we need to select on row number due to sorted columns.
      } else if (!this.readonly && event.shiftKey && this.rowSelection.length) {
        this.shiftSelect(row);

      // Simple click sets the clicked row as the rowSelection.
      } else {
        this.rowSelection = [row.id];
      }
    },

    clearRowSelection() {
      this.$nextTick(() => {
        const draggable = document.getElementsByTagName('tr');
        [].forEach.call(draggable, (element) => {
          element.removeAttribute('draggable');
        });
      });
      this.rowSelection = [];
    },

    // Applies a css class to selected rows.
    applyRowClass(row) {
      if (row.ext !== 'parent' && row.type !== 'regular') {
        if ((this.extension && row.ext !== this.extension) || (this.fileType && this.fileType !== row.type)) {
          return 'ctrl-row-disabled';
        }
      }
      if (this.dragging && row.id === this.dropTarget && row.parent_id === null) {
        return null;
      } if (this.dragging && row.id === this.dropTarget) {
        return 'drop-test';
      } if (this.dragging && this.rowSelection.indexOf(row.id) > -1) {
        return 'ctrl-row-select grab';
      } if (this.rowSelection.indexOf(row.id) > -1 && row.ext) {
        return 'ctrl-row-select draggable-files';
      } if (this.rowSelection.indexOf(row.id) > -1) {
        return 'ctrl-row-select';
      }
      if (row.ext === 'parent' || row.type === 'regular') {
        if ((this.extension && row.ext !== this.extension) || (this.fileType && this.fileType !== row.type)) {
          return 'ctrl-row-select;';
        }
      }
      return '';
    },

    /**
     * On click on one of the directories in the tree the right
     * content is fetched by the following function
    */
    fetchContent(data) {
      this.activeDir = data;
      // Empty selectRows array
      this.clearRowSelection();
      // Empty locations array
      this.locations = [];
      // Cut out string 'api' for the right api call
      const url = data.location.replace('api', '');

      this.apiUrl = url;
      EventBus.$emit('library-table:fetch-content', data);
    },

    // Set draggable = true after class has been applied
    setDraggableRows() {
      setTimeout(() => {
        const draggable = document.getElementsByClassName('draggable-files');
        [].forEach.call(draggable, (element) => {
          element.setAttribute('draggable', true);
        });
      }, 100);
    },

    // Add folder
    async addFolder(newFolderName) {
      const url = this.currentFolderSelected ? this.currentFolderSelected.location.replace('api', '') : this.apiUrl;
      try {
        await this.axios.post(url, qs.stringify({ name: newFolderName }));
        EventBus.$emit('library-table:modify-rows');
        // notify user
        this.qNotify('module.library.success.add.folder', 'positive', 'top-right');
        this.$nextTick(() => {
          this.$refs.vuetable && this.$refs.vuetable.refresh();
        });
      } catch {
        // Folder already exists, prompt again
        this.promptNewFolder();
        // notify user
        this.qNotify('module.library.error.exists.folder', 'negative', 'bottom');
      }
    },

    // Open dialog to ask user if they are sure to delete the selected properties
    checkDeleteProperties() {
      // Define confirmMessage for single or multiple folders/files
      const confirmMessage = (this.rowSelection.length > 1) ? getString('module.library.delete.title.multiple') : getString('module.library.delete.title.single');
      // Open dialog
      this.$q.dialog({
        message: confirmMessage,
        ok: getString('dialog.confirm.ok'),
        cancel: getString('dialog.confirm.cancel'),
        color: 'primary',
      }).then(() => {
        // Clicked OK, delete properties
        this.deleteProperties();
      });
    },

    // Delete properties
    deleteProperties() {
      // define local vars
      let finished = '';
      let url = '';
      const arr = [];
      // Set all array-properties to false
      for (let i = 0; i < this.rowSelection.length; i += 1) {
        arr[i] = false;
      }
      // Function fired from .every. Gives abck true if val is 204 (response from axios)
      const checkValues = status => status === 204;

      // Loop trough rowSelection to add id's to array
      this.rowSelection.forEach(async (id, index) => {
        // Build up url for delete call
        url = this.locations[id].location.replace('api', '');

        // Store api-call in var and push status to arr -array
        finished = await this.axios.delete(url);
        // Push statuses to arr
        arr[index] = finished.status;
        // Check if all pushed statuses are 204 (true), then reload tree
        if (arr.every(checkValues)) {
          this.rowSelection = [];
          // All succesfull, reload tree and folder content and notify user
          // Define confirmNotify for single or multiple folders/files
          const confirmNotify = (this.rowSelection.length > 1) ? getString('module.library.delete.deleted.multiple') : getString('module.library.delete.deleted.single');

          // Refresh library
          this.$nextTick(() => {
            this.$refs.vuetable && this.$refs.vuetable.refresh();
          });

          EventBus.$emit('library-table:modify-rows');

          // notify user
          this.$q.notify({
            message: confirmNotify,
            color: 'positive',
            type: 'positive',
            position: 'top-right',
          });
        }
      });
    },


    // Function to rename property
    promptRenameProperty() {
      // Define propmptMessage for file or folder rename
      let promptMessage = '';
      let type;

      let modelName = '';
      let model = {};
      if (this.currentFolderSelected && this.currentFolderSelected.name) {
        model = this.currentFolderSelected;
        modelName = this.currentFolderSelected.name;
      } else if (this.activeDir && this.activeDir.name) {
        model = this.activeDir;
        modelName = this.activeDir.name;
      }

      // Check if property 'ext' exists. If so we are dealing with a file, otherwise it is a folder
      if (this.$refs.vuetable.tableData && this.$refs.vuetable.tableData[this.rowSelection[0]] && this.$refs.vuetable.tableData[this.rowSelection[0]].ext) {
        // Set promptMessage for file
        promptMessage = getString('module.library.file.details.edit.rename');
        // Set type to file
        type = 'file';
        // First we need to extract the extension from the filename so the user is not able to change it
        model = this.$refs.vuetable.tableData[this.rowSelection[0]];
        modelName = this.$refs.vuetable.tableData[this.rowSelection[0]].name.replace(/\.[^/.]+$/, '');
      } else {
        // Set promptMessage for folder
        promptMessage = getString('module.library.folder.details.edit.rename');
        // Set type to folder
        type = 'folder';
      }

      // Open dialog to rename file or folder
      this.prompt = true;
      this.$q.dialog({
        message: promptMessage,
        prompt: {
          model: modelName,
          type: 'text',
        },
        ok: getString('rename'),
        preventClose: true,
        cancel: getString('dialog.confirm.cancel'),
        color: 'primary',
      }).then((data) => {
        // Set loadingState to true
        this.isLoading = true;
        // Clicked ok -> rename
        this.renameFile(model, data, type);
      }).catch((err) => {
        console.log(err);
        // Clicked cancel
        console.warn('Cancelled renaming of property');
      }).finally(() => {
        this.prompt = false;
      });
    },

    // Rename file
    renameFile(originalObj, newName, type) {
      const name = type === 'folder' ? newName : `${newName}.${originalObj.ext}`;

      // Check if name is the same that it already is
      if (originalObj.name === name) {
        this.qNotify('Same name, nothing is saved', 'info', 'center', false, { timeout: 1000 });
        this.$nextTick(() => {
          this.$refs.vuetable && this.$refs.vuetable.refresh();
        });
        return;
      }
      // Apply rename
      this.applyRename(originalObj, name, type);
    },

    async applyRename(originalObj, newName, type) {
      // Determine param and to send in the request
      const name = type === 'folder' ? { name: newName } : { filename: newName };
      try {
        await this.axios.put(originalObj.location.replace('api', ''), qs.stringify(name), { headers: { 'Content-Disposition': 'inline' } });
        this.qNotify(`module.library.${type}.details.edit.success`, 'positive', 'top-right');
      } catch (error) {
        // Already exists, notify user and prompt again
        this.qNotify(`module.library.error.exists.${type}`, 'negative', 'bottom');
        this.promptRenameProperty();

      // Refresh the library
      } finally {
        this.$nextTick(() => {
          if (originalObj.type === 'regular') {
            // Update the api url with the new folder name
            const path = this.$store.state.libraryStore.libraryPath.replace(originalObj.name, newName);
            this.apiUrl = path;
          }
          EventBus.$emit('library-table:modify-rows');
          this.$refs.vuetable && this.$refs.vuetable.refresh();
        });
      }
    },

    /**
     * Functions to add a new folder in activeDir
     */
    promptNewFolder() {
      this.prompt = true;
      // Define const for new folder name
      const newFolderName = '';

      let currentFolderName = 'Root';
      if (this.currentFolderSelected && this.currentFolderSelected.name) {
        currentFolderName = this.currentFolderSelected.name;
      } else if (this.activeDir && this.activeDir.name) {
        currentFolderName = this.activeDir.name;
      }
      // Open dialog to rename file or folder
      this.$q.dialog({
        message: `${getString('module.library.addFolder')} in: ${currentFolderName}`,
        prompt: {
          model: newFolderName,
          type: 'text',
        },
        ok: getString('action.add'),
        preventClose: true,
        cancel: getString('dialog.confirm.cancel'),
        color: 'primary',
      }).then((data) => {
        // Clicked ok -> rename
        this.addFolder(data);
        // Set loadingstate to true
        this.isLoading = true;
      }).catch(() => {
        // Clicked cancel
        this.newFolderName = '';
      }).finally(() => {
        this.prompt = false;
      });
    },

    keyBoardSelectRow(e) {
      // Ignore keyboard input when dialog is open.
      if (this.prompt) return;

      let row = this.rowSelection.length ? this.locations[this.rowSelection] : null;

      switch (e.code) {
        //
        case 'ArrowUp':
          // If the selected row index is greater than 0, decrease the row index by 1.
          if (this.rowSelection[0] > 0) {
            this.rowSelection = [this.rowSelection.pop() - 1];
            row = this.locations[this.rowSelection[0]];
            if (row.ext && this.readonly) {
              EventBus.$emit('library-table:preview-image', row);
            }
          }
          if (this.rowSelection.length === 0) {
            this.rowSelection = [0];
          }
          break;
        case 'ArrowDown':
          // If the selected row index is less than the number of rows, increase the row index by 1.
          if (this.rowSelection[0] < this.locations.length - 1) {
            this.rowSelection = [this.rowSelection.pop() + 1];
            row = this.locations[this.rowSelection[0]];
            if (row.ext && this.readonly) {
              EventBus.$emit('library-table:preview-image', row);
            }
          }
          if (this.rowSelection.length === 0) {
            this.rowSelection = [0];
          }
          break;
        case 'Enter':
          if (this.rowSelection.length === 1) {
            row = this.locations[this.rowSelection[0]];
            if (!row.ext || row.ext === 'parent') {
              this.fetchContent(row);
            } else if (this.readonly) {
              this.readonly && EventBus.$emit('library-table:select-row');
            } else {
              this.goToDetail(row);
            }
          }
          break;
        default:
          break;
      }
    },

    qNotify(msg, color, position, stringsStore = true, options = {}) {
      this.$q.notify(Object.assign({
        message: stringsStore ? getString(msg) : msg,
        color,
        position,
      }, options));
    },
  },

  mounted() {
    document.addEventListener('keydown', this.keyBoardSelectRow);
    EventBus.$on('refresh-table', () => {
      this.rowSelection = [];
      this.$nextTick(() => {
        this.$refs.vuetable && this.$refs.vuetable.refresh();
      });
    });
    EventBus.$on('library:fetch-tree', (payload) => {
      this.fetchContent(payload);
    });
    EventBus.$on('library:upload', () => {
      this.$refs.buttonUpload.click();
    });
    EventBus.$on('library:new-folder', () => {
      this.promptNewFolder();
    });
    EventBus.$on('library:rename', () => {
      this.promptRenameProperty();
    });
    EventBus.$on('library:delete', () => {
      this.checkDeleteProperties();
    });
    this.initTableEvents();
    //  Determine if drag and drop functionality is capable in the browser
    if (this.determineDragAndDropCapable()) {
      // Init dropzone
      this.dropZoneInit();
    }
  },

  watch: {
    rowSelection() {
      // Determine if the selected row is linked to the parent folder.
      const parent = this.locations.length && this.locations[this.rowSelection[0]] && this.locations[this.rowSelection[0]].ext === 'parent';

      // Update the row selection for related components.
      EventBus.$emit('library-table:row-selection', { selection: this.rowSelection, parent });
    },
  },

  destroyed() {
    EventBus.$off('refresh-table');
    EventBus.$off('library:upload');
    EventBus.$off('library:new-folder');
    EventBus.$off('library:rename');
    EventBus.$off('library:delete');
    EventBus.$off('library:fetch-tree');
    document.removeEventListener('keydown', this.keyBoardSelectRow);
    this.removeTableEvents();
  },
};
</script>

<style lang="stylus">
.ctrl-row-disabled
  > td
    opacity 0.5
    cursor not-allowed

.library-table__file-info
  background #fff
  padding 10px
  position fixed
  border 1px #d3d3d3 solid
  &--row
    font-size 14px
    margin-bottom 2px
  &--bold
    font-weight bold
</style>
